import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import IconChevronLeftMedium from "@amzn/stencil-react-icons/icons/icon-chevron-left-medium";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { PropsWithChildren, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

export interface RecordStepContainerProps extends PropsWithChildren<unknown> {
  /**
   * Reset the selected step to go back to the FRIV view.
   */
  clearSelectedStep: () => void;

  /**
   * The Snowplow event label that will be sent through Snowplow
   */
  eventLabel:
    | Day1EventLabel.PrimaryId
    | Day1EventLabel.SecondaryId
    | Day1EventLabel.ChallengeAction;
}

/**
 * A view that wraps the detail view of each record step with a back button.
 * @param props A RecordStepContainerProps interface
 * @constructor
 */
export default function RecordStepContainer({
  clearSelectedStep,
  eventLabel,
  children,
}: RecordStepContainerProps): JSX.Element {
  const intl = useIntl();
  const { state, dispatch } = useContext(AppContext);

  /**
   * Clear the current selected step and remove the last recorded document if NH did not click the submit button.
   */
  const handleBack = () => {
    if (
      state.documents.length > 0 &&
      !state.documents[state.documents.length - 1].submitClicked
    ) {
      dispatch({
        type: ActionType.POP_DOCUMENT,
      });
    }

    trackStructEvent({
      category: Day1EventCategory.IDCheckFull,
      action: Day1EventAction.BackToRivList,
      label: eventLabel,
    });

    clearSelectedStep();
  };

  return (
    <Col gridGap="S400">
      <Row margin={{ left: 0 }} padding={{ left: 0 }}>
        <Button
          className="full-id-back-btn"
          dataTestId="recordStepBackBtn"
          icon={
            <IconChevronLeftMedium
              title={intl.formatMessage({
                id: "identityCheck-button-goBackAltText",
              })}
              color="#007C98"
              dataTestId="iconChevronLeftDataTestId"
            />
          }
          variant={ButtonVariant.Tertiary}
          onClick={handleBack}
        >
          <Text fontWeight="regular" color="#007C98">
            <FormattedMessage id="button-back" />
          </Text>
        </Button>
      </Row>
      {children}
    </Col>
  );
}
